import React, { useEffect } from "react";
import { X } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useOrderPaymentMutation } from "../../../State/Api/PharmacyOrderApiSlice";

const PurchasePaymentModal = ({
  show,
  onHide,
  totalPayable = 0,
  onSubmit,
  distributorName,
  invoiceNum,
  invoiceDate,
  billId
}) => {
  const [orderPayment, { isLoading }] = useOrderPaymentMutation();
  const navigate = useNavigate();
  const pharmaID = localStorage.getItem("userUniqueID") || "string";

  // ✅ Initialize Formik
  const formik = useFormik({
    initialValues: {
      payingCustomerName: "",
      invoiceNum: "",
      invoiceDate: "",
      paymentMode: "PAID/CASH",
      transactionType: "Purchase",
      amountPaid: "",
    },
    validationSchema: Yup.object({
      payingCustomerName: Yup.string()
        .required("Paying customer name is required")
        .min(2, "Name is too short"),
      amountPaid: Yup.number()
        .required("Amount is required")
        .min(0, "Amount must be positive"),
    }),
    onSubmit: async (values) => {
      const paymentPayload = {
        paymentID: 0,
        pharmaID,
       billID: billId || 0,
        payingCustomerName: values.payingCustomerName,
        paymentMode: values.paymentMode,
        transactionType: values.transactionType,
        invoiceDate: values.invoiceDate,
        invoiceNum: values.invoiceNum,
        amountPaid: parseFloat(values.amountPaid),
        payableAmount: parseFloat(totalPayable),
        balanceDue:
          parseFloat(totalPayable) - parseFloat(values.amountPaid || 0),
        createdBy: "pharma",
        updatedBy: "pharma",
        isActive: true,
      };

      try {
        await orderPayment(paymentPayload).unwrap();
        toast.success("💰 Payment saved successfully!", {
          duration: 3000,
          position: "top-center",
          style: { background: "#10b981", color: "#fff", fontWeight: 600 },
        });

        setTimeout(() => navigate("/PharmacyPurchase1"), 1000);
        handleClose();
      } catch (err) {
        console.error("❌ Payment error:", err);
        toast.error("Failed to save payment!", {
          duration: 3000,
          position: "top-center",
          style: { background: "#ef4444", color: "#fff", fontWeight: 600 },
        });
      }
    },
  });

  useEffect(() => {
    if (show) {
      const today = new Date();
      const isoDate = invoiceDate || today.toISOString(); // Prefer parent’s date if available
      const autoInvoice =
        invoiceNum || `INV-${today.getTime().toString().slice(-6)}`;

      formik.setValues((prev) => ({
        ...prev,
        payingCustomerName: distributorName || "",
        invoiceNum: autoInvoice,
        invoiceDate: isoDate,
        amountPaid: Number(totalPayable || 0).toFixed(2),
      }));
    }
  }, [show, totalPayable, distributorName, invoiceNum, invoiceDate]);

  if (!show) return null;

  const handleClose = () => {
    formik.resetForm();
    onHide();
  };

  const numericPayable = Number(totalPayable) || 0;
  const payingNow = Number(formik.values.amountPaid) || 0;
  const balanceDue = numericPayable - payingNow;

  return (
    <>
      <Toaster />
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "500px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#2c3e50",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2
                style={{
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                Add Payment
              </h2>
              <p
                style={{
                  color: "#bdc3c7",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                Fill in the details below to add payment
              </p>
            </div>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button
                onClick={formik.handleSubmit}
                disabled={isLoading}
                style={{
                  background: "#2c3e50",
                  color: "#fff",
                  border: "1px solid #fff",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {isLoading ? "Saving..." : "Continue (F2)"}
              </button>
              <button
                onClick={handleClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={20} color="#fff" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "24px 20px" }}>
            {/* Paying Customer */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>PAYING Distributor</label>
              <input
                type="text"
                name="payingCustomerName"
                value={formik.values.payingCustomerName}
                onChange={formik.handleChange}
                readOnly
                style={{
                  ...inputStyle,
                  background: "#f3f4f6",
                  color: "#6b7280",
                  cursor: "not-allowed",
                }}
              />

              {formik.touched.payingCustomerName &&
                formik.errors.payingCustomerName && (
                  <span style={errorText}>
                    {formik.errors.payingCustomerName}
                  </span>
                )}
            </div>

            {/* Invoice Number + Date */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label style={labelStyle}>INVOICE NUMBER</label>
                <input
                  type="text"
                  name="invoiceNum"
                  value={formik.values.invoiceNum}
                  readOnly
                  style={{
                    ...inputStyle,
                    background: "#f3f4f6",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>INVOICE DATE</label>
                <input
                  type="text"
                  name="invoiceDate"
                  value={new Date(formik.values.invoiceDate).toLocaleDateString(
                    "en-GB"
                  )}
                  readOnly
                  style={{
                    ...inputStyle,
                    background: "#f9fafb",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            </div>

            {/* Payment Mode + Transaction Type */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label style={labelStyle}>PAYMENT TYPE</label>
                <div style={{ display: "flex", gap: "12px" }}>
                  {["UNPAID/CREDIT", "PAID/CASH"].map((type) => (
                    <label
                      key={type}
                      style={radioStyle(
                        formik.values.paymentMode === type,
                        type === "PAID/CASH" ? "#10b981" : "#0ea5e9",
                        type === "PAID/CASH" ? "#d1fae5" : "#e0f2fe"
                      )}
                    >
                      <input
                        type="radio"
                        name="paymentMode"
                        value={type}
                        checked={formik.values.paymentMode === type}
                        onChange={formik.handleChange}
                      />
                      {type === "PAID/CASH" ? "Cash" : "Credit"}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>TRANSACTION TYPE</label>
                <select
                  name="transactionType"
                  value={formik.values.transactionType}
                  onChange={formik.handleChange}
                  style={{
                    ...inputStyle,
                    cursor: "pointer",
                    appearance: "auto",
                  }}
                >
                  <option value="Purchase">Purchase</option>
                  <option value="Sale">Sale</option>
                  <option value="Customer">Customer</option>
                  <option value="Distributor">Distributor</option>
                </select>
              </div>
            </div>

            {/* Amount Paid */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>
                AMOUNT PAID <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  ₹
                </span>
                <input
                  type="number"
                  name="amountPaid"
                  value={formik.values.amountPaid}
                  onChange={formik.handleChange}
                  style={{ ...inputStyle, paddingLeft: "28px" }}
                />
              </div>
              {formik.touched.amountPaid && formik.errors.amountPaid && (
                <span style={errorText}>{formik.errors.amountPaid}</span>
              )}
            </div>

            {/* Summary */}
            <div
              style={{
                background: "#fef3c7",
                borderRadius: "8px",
                padding: "16px",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
              }}
            >
              <SummaryBox
                title="PAYABLE AMT"
                value={`₹${numericPayable.toFixed(2)}`}
                color="#1f2937"
              />
              <SummaryBox
                title="PAYING NOW"
                value={`₹${payingNow.toFixed(2)}`}
                color="#10b981"
              />
              <SummaryBox
                title="BALANCE DUE"
                value={balanceDue > 0 ? `₹${balanceDue.toFixed(2)}` : "—"}
                color="#1f2937"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Styles
const labelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#2c3e50",
  display: "block",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "2px solid #e5e7eb",
  borderRadius: "6px",
  fontSize: "14px",
  outline: "none",
  background: "#f9fafb",
};
const errorText = {
  color: "#ef4444",
  fontSize: "11px",
  marginTop: "4px",
  display: "block",
};
const radioStyle = (active, color, bg) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  fontWeight: 500,
  padding: "6px 10px",
  borderRadius: "6px",
  border: `2px solid ${active ? color : "#e5e7eb"}`,
  background: active ? bg : "#f9fafb",
  cursor: "pointer",
  transition: "all 0.2s",
});
const SummaryBox = ({ title, value, color }) => (
  <div>
    <div
      style={{
        fontSize: "10px",
        fontWeight: 700,
        color: "#92400e",
        marginBottom: "6px",
        textTransform: "uppercase",
      }}
    >
      {title}
    </div>
    <div style={{ fontSize: "16px", fontWeight: 700, color }}>{value}</div>
  </div>
);

export default PurchasePaymentModal;
